import { FC, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { apiClient } from "../../utils/apiClient";
import { format } from "date-fns";
import { compactTableStyles, codeStyles } from "../theme";
import CreateRecurringWorkflowModal from "./CreateRecurringWorkflowModal";
import { useConfirmationDialog } from "../../components/ConfirmationDialog";
import TokenDisplay from "../../components/TokenDisplay";

interface RecurringWorkflow {
  id: string;
  workflowName: string;
  schedule: string;
  waitForPrevious: boolean;
  enabled: boolean;
  state: any;
  initialParams: any;
  lastRunAt: string | null;
  nextRunAt: string | null;
  currentWorkflowId: string | null;
  userId: string | null;
  startTime: string | null;
  startDelay: number;
  phaseSec: number;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowType {
  name: string;
  description?: string;
  testData: Array<{
    name: string;
    description?: string;
    data: any;
  }>;
}

interface TemporalConfig {
  temporalUiUrl: string;
  namespace: string;
  taskQueue: string;
}

interface WorkflowRegistryResponse {
  workflows: WorkflowType[];
  temporalConfig: TemporalConfig;
}

const RecurringWorkflowsTab: FC = () => {
  const navigate = useNavigate();
  const { openDialog, openAlert, ConfirmationDialog } = useConfirmationDialog();
  const [workflows, setWorkflows] = useState<RecurringWorkflow[]>([]);
  const [workflowTypes, setWorkflowTypes] = useState<WorkflowType[]>([]);
  const [temporalConfig, setTemporalConfig] = useState<TemporalConfig>({
    temporalUiUrl: "http://localhost:8080", // fallback
    namespace: "default",
    taskQueue: "dobby-workflows",
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<RecurringWorkflow | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [isCreatingOneTime, setIsCreatingOneTime] = useState(false);

  // Form state (for edit dialog only)
  const [schedule, setSchedule] = useState("@every 1h");
  const [waitForPrevious, setWaitForPrevious] = useState(true);
  const [initialParams, setInitialParams] = useState("{}");
  const [startTime, setStartTime] = useState<string>("");
  const [startDelay, setStartDelay] = useState<number>(0);
  const [phaseSec, setPhaseSec] = useState<number>(0);
  const [timingMode, setTimingMode] = useState<
    "none" | "startTime" | "startDelay" | "phaseSec"
  >("none");

  // Fetch recurring workflows
  const fetchWorkflows = useCallback(async () => {
    try {
      const response = await apiClient.get<{ workflows: RecurringWorkflow[] }>(
        "/api/workflows/recurring-workflows"
      );
      setWorkflows(response.workflows);
    } catch (error) {
      console.error("Error fetching recurring workflows:", error);
    }
  }, []);

  // Fetch workflow types
  const fetchWorkflowTypes = useCallback(async () => {
    try {
      const response = await apiClient.get<WorkflowRegistryResponse>(
        "/api/workflows/workflow-registry"
      );
      setWorkflowTypes(response.workflows);
      setTemporalConfig(response.temporalConfig);
    } catch (error) {
      console.error("Error fetching workflow types:", error);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
    fetchWorkflowTypes();
  }, [fetchWorkflows, fetchWorkflowTypes]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    workflow: RecurringWorkflow
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedWorkflow(workflow);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // Don't clear selectedWorkflow here - it's needed for dialogs
  };

  const handlePauseResume = async () => {
    if (!selectedWorkflow) return;
    handleMenuClose();

    try {
      const endpoint = selectedWorkflow.enabled ? "pause" : "resume";
      await apiClient.post(
        `/api/workflows/recurring-workflows/${selectedWorkflow.id}/${endpoint}`
      );
      fetchWorkflows();
    } catch (error) {
      console.error(
        `Failed to ${selectedWorkflow.enabled ? "pause" : "resume"} workflow:`,
        error
      );
    }
  };

  const handleEdit = (workflow?: RecurringWorkflow) => {
    const workflowToEdit = workflow || selectedWorkflow;
    if (!workflowToEdit) return;
    handleMenuClose();
    setSelectedWorkflow(workflowToEdit);
    setEditDialogOpen(true);
    setSchedule(workflowToEdit.schedule);
    setWaitForPrevious(workflowToEdit.waitForPrevious);
    setInitialParams(JSON.stringify(workflowToEdit.initialParams, null, 2));
    setStartTime(workflowToEdit.startTime || "");
    setStartDelay(workflowToEdit.startDelay || 0);
    setPhaseSec(workflowToEdit.phaseSec || 0);

    // Set timing mode based on existing values
    if (workflowToEdit.startTime) {
      setTimingMode("startTime");
    } else if (workflowToEdit.startDelay > 0) {
      setTimingMode("startDelay");
    } else if (workflowToEdit.phaseSec > 0) {
      setTimingMode("phaseSec");
    } else {
      setTimingMode("none");
    }
  };

  const handleRunNow = async () => {
    if (!selectedWorkflow) return;
    handleMenuClose();

    const isOneTime = selectedWorkflow.schedule === "one-time";
    const actionText = isOneTime ? "run again" : "trigger to run now";
    const confirmText = isOneTime ? "Run Again" : "Run Now";
    const title = isOneTime ? "Run Workflow Again" : "Trigger Workflow";

    openDialog(
      `Are you sure you want to ${actionText} "${selectedWorkflow.workflowName}"?`,
      async () => {
        try {
          const result = await apiClient.post<{ workflowId: string }>(
            `/api/workflows/recurring-workflows/${selectedWorkflow.id}/trigger`
          );

          const successMessage = isOneTime
            ? `Workflow started again successfully! Workflow ID: ${result.workflowId}`
            : `Workflow triggered successfully! Schedule ID: ${result.workflowId}`;

          openAlert(successMessage, {
            title: "Success",
            confirmColor: "success",
          });
          fetchWorkflows();
        } catch (error: any) {
          console.error("Failed to trigger workflow:", error);
          openAlert(error.message || "Failed to trigger workflow", {
            title: "Error",
            confirmColor: "error",
          });
        }
      },
      {
        title,
        confirmText,
        confirmColor: "primary",
      }
    );
  };

  const handleUpdate = async () => {
    if (!selectedWorkflow) return;

    try {
      let params = {};
      try {
        params = JSON.parse(initialParams);
      } catch (error) {
        openAlert("Invalid JSON in initial parameters", {
          title: "Error",
          confirmColor: "error",
        });
        return;
      }

      // Prepare update payload
      const updatePayload: any = {
        schedule,
        waitForPrevious,
        initialParams: params,
      };

      // Only include timing fields based on selected mode
      if (timingMode === "startTime" && startTime) {
        updatePayload.startTime = startTime;
        updatePayload.startDelay = 0;
        updatePayload.phaseSec = 0;
      } else if (timingMode === "startDelay") {
        updatePayload.startTime = null;
        updatePayload.startDelay = startDelay;
        updatePayload.phaseSec = 0;
      } else if (timingMode === "phaseSec") {
        updatePayload.startTime = null;
        updatePayload.startDelay = 0;
        updatePayload.phaseSec = phaseSec;
      } else {
        // Clear all timing fields
        updatePayload.startTime = null;
        updatePayload.startDelay = 0;
        updatePayload.phaseSec = 0;
      }

      await apiClient.patch(
        `/api/workflows/recurring-workflows/${selectedWorkflow.id}`,
        updatePayload
      );

      setEditDialogOpen(false);
      fetchWorkflows();
    } catch (error: any) {
      console.error("Failed to update recurring workflow:", error);
      openAlert(error.message || "Failed to update recurring workflow", {
        title: "Error",
        confirmColor: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkflow) return;
    handleMenuClose();

    openDialog(
      `Are you sure you want to delete this recurring workflow?`,
      async () => {
        try {
          await apiClient.delete(
            `/api/workflows/recurring-workflows/${selectedWorkflow.id}`
          );
          fetchWorkflows();
        } catch (error) {
          console.error("Failed to delete workflow:", error);
        }
      },
      {
        title: "Delete Workflow",
        confirmText: "Delete",
        confirmColor: "error",
      }
    );
  };

  const handleTerminate = async () => {
    if (!selectedWorkflow) return;
    handleMenuClose();

    openDialog(
      `Are you sure you want to terminate "${selectedWorkflow.workflowName}"? This will forcefully stop the running workflow.`,
      async () => {
        try {
          await apiClient.post(
            `/api/workflows/recurring-workflows/${selectedWorkflow.id}/terminate`
          );
          openAlert("Workflow terminated successfully", {
            title: "Success",
            confirmColor: "success",
          });
          fetchWorkflows();
        } catch (error: any) {
          console.error("Failed to terminate workflow:", error);
          openAlert(error.message || "Failed to terminate workflow", {
            title: "Error",
            confirmColor: "error",
          });
        }
      },
      {
        title: "Terminate Workflow",
        confirmText: "Terminate",
        confirmColor: "error",
      }
    );
  };

  const handleViewState = (workflow?: RecurringWorkflow) => {
    if (workflow) {
      setSelectedWorkflow(workflow);
    }
    if (!workflow && !selectedWorkflow) return;
    handleMenuClose();
    setStateDialogOpen(true);
  };

  const handleViewTemporalWorkflows = () => {
    if (!selectedWorkflow) return;
    handleMenuClose();

    // Filter by workflow ID pattern since search attributes are not available
    const searchQuery = `WorkflowId LIKE "${selectedWorkflow.workflowName}-${selectedWorkflow.id}-%"`;
    const url = `${temporalConfig.temporalUiUrl}/namespaces/${temporalConfig.namespace}/workflows?query=${encodeURIComponent(searchQuery)}`;
    window.open(url, "_blank");
  };

  const handleViewGptRequests = () => {
    if (!selectedWorkflow) return;
    handleMenuClose();
    navigate(`/admin/requests?recurringWorkflowId=${selectedWorkflow.id}`);
  };

  const handleViewActionLogs = () => {
    if (!selectedWorkflow) return;
    handleMenuClose();
    navigate(`/admin/action-logs?recurringWorkflowId=${selectedWorkflow.id}`);
  };

  const handleRowClick = (workflow: RecurringWorkflow) => {
    if (workflow.schedule === "one-time" && workflow.currentWorkflowId) {
      // For one-time workflows, open the specific workflow execution
      const workflowUrl = `${temporalConfig.temporalUiUrl}/namespaces/${temporalConfig.namespace}/workflows/${workflow.currentWorkflowId}`;
      window.open(workflowUrl, "_blank");
    } else if (workflow.schedule === "one-time") {
      // If no current workflow ID, search for workflows
      const searchQuery = `WorkflowId LIKE "${workflow.workflowName}-${workflow.id}-%"`;
      const url = `${temporalConfig.temporalUiUrl}/namespaces/${temporalConfig.namespace}/workflows?query=${encodeURIComponent(searchQuery)}`;
      window.open(url, "_blank");
    } else {
      // For recurring workflows, open the schedule view
      const scheduleId = `recurring-${workflow.id}`;
      const url = `${temporalConfig.temporalUiUrl}/namespaces/${temporalConfig.namespace}/schedules/${scheduleId}`;
      window.open(url, "_blank");
    }
  };

  const handleDeleteAll = async () => {
    openDialog(
      `Are you sure you want to delete ALL ${workflows.length} recurring workflows? This action cannot be undone.`,
      async () => {
        try {
          await apiClient.delete("/api/workflows/recurring-workflows/all");
          fetchWorkflows();
        } catch (error) {
          console.error("Failed to delete all workflows:", error);
          openAlert("Failed to delete workflows. Please try again.", {
            title: "Error",
            confirmColor: "error",
          });
        }
      },
      {
        title: "Delete All Workflows",
        confirmText: "Delete All",
        confirmColor: "error",
      }
    );
  };

  const handleDeleteAllOneOffs = async () => {
    const oneOffWorkflows = workflows.filter((w) => w.schedule === "one-time");

    if (oneOffWorkflows.length === 0) {
      openAlert("No one-off workflows to delete.", {
        title: "Information",
        confirmColor: "info",
      });
      return;
    }

    openDialog(
      `Are you sure you want to delete ALL ${oneOffWorkflows.length} one-off workflows? This action cannot be undone.`,
      async () => {
        try {
          // Delete each one-off workflow individually
          for (const workflow of oneOffWorkflows) {
            await apiClient.delete(
              `/api/workflows/recurring-workflows/${workflow.id}`
            );
          }
          fetchWorkflows();
        } catch (error) {
          console.error("Failed to delete one-off workflows:", error);
          openAlert("Failed to delete one-off workflows. Please try again.", {
            title: "Error",
            confirmColor: "error",
          });
        }
      },
      {
        title: "Delete All One-Off Workflows",
        confirmText: "Delete All",
        confirmColor: "error",
      }
    );
  };

  return (
    <Box>
      {/* Header with Create button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Recurring Workflows</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeleteSweepIcon />}
              onClick={handleDeleteAllOneOffs}
              disabled={
                workflows.filter((w) => w.schedule === "one-time").length === 0
              }
            >
              Delete All One-Offs
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleDeleteAll}
              disabled={workflows.length === 0}
            >
              Delete All
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsCreatingOneTime(false);
                setCreateDialogOpen(true);
              }}
            >
              Create Recurring Workflow
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsCreatingOneTime(true);
                setCreateDialogOpen(true);
              }}
            >
              Create One-time
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Workflows Table */}
      <TableContainer component={Paper}>
        <Table {...compactTableStyles.table}>
          <TableHead sx={compactTableStyles.tableHead}>
            <TableRow>
              <TableCell>Workflow Name</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Initial Params</TableCell>
              <TableCell>Tokens</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell>Current Run</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={compactTableStyles.tableBody}>
            {workflows.map((workflow) => (
              <TableRow
                key={workflow.id}
                onClick={() => handleRowClick(workflow)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={workflow.workflowName} placement="top">
                    <span>{workflow.workflowName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box component="code" sx={codeStyles.small}>
                    {workflow.schedule}
                  </Box>
                </TableCell>
                <TableCell>
                  {workflow.schedule === "one-time" ? (
                    <Chip
                      label="N/A"
                      color="default"
                      size="small"
                      sx={compactTableStyles.chip}
                    />
                  ) : (
                    <Chip
                      label={workflow.enabled ? "Active" : "Paused"}
                      color={workflow.enabled ? "success" : "default"}
                      size="small"
                      icon={
                        workflow.enabled ? <PlayArrowIcon /> : <PauseIcon />
                      }
                      sx={compactTableStyles.chip}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {workflow.state?.progress ? (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.85em",
                        fontFamily: "monospace",
                        color: "text.primary",
                      }}
                    >
                      {workflow.state.progress}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.85em",
                      }}
                    >
                      -
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {/* Actions */}
                  {workflow.state && Object.keys(workflow.state).length > 0 ? (
                    <Tooltip
                      title={
                        <pre
                          style={{
                            margin: 0,
                            fontSize: 12,
                            maxWidth: 400,
                            overflow: "auto",
                          }}
                        >
                          {(() => {
                            const stateStr = JSON.stringify(
                              workflow.state,
                              null,
                              2
                            );
                            const maxLength = 1000; // Max characters in tooltip
                            return stateStr.length > maxLength
                              ? stateStr.substring(0, maxLength) +
                                  "\n...\n(truncated - click to view full state)"
                              : stateStr;
                          })()}
                        </pre>
                      }
                      placement="left"
                    >
                      <Box
                        component="span"
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "primary.main",
                          fontSize: "0.85em",
                          fontFamily: "monospace",
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleViewState(workflow);
                        }}
                      >
                        {JSON.stringify(workflow.state).substring(0, 10)}...
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85em",
                      }}
                    >
                      {"{}"}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {workflow.initialParams &&
                  Object.keys(workflow.initialParams).length > 0 ? (
                    <Tooltip
                      title={
                        <pre style={{ margin: 0, fontSize: 12 }}>
                          {JSON.stringify(workflow.initialParams, null, 2)}
                        </pre>
                      }
                      placement="left"
                    >
                      <Box
                        component="span"
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "primary.main",
                          fontSize: "0.85em",
                          fontFamily: "monospace",
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleEdit(workflow); // Open edit dialog to view/edit params
                        }}
                      >
                        {JSON.stringify(workflow.initialParams).substring(
                          0,
                          10
                        )}
                        ...
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85em",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click
                        handleEdit(workflow); // Open edit dialog to add params
                      }}
                    >
                      {"{}"}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {workflow.state?.promptTokens ||
                  workflow.state?.outputTokens ? (
                    <TokenDisplay
                      promptTokens={workflow.state?.promptTokens || 0}
                      outputTokens={workflow.state?.outputTokens || 0}
                      variant="arrows"
                    />
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.85em",
                      }}
                    >
                      -
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {workflow.lastRunAt
                    ? format(new Date(workflow.lastRunAt), "MMM d, HH:mm")
                    : "-"}
                </TableCell>
                <TableCell>
                  {workflow.nextRunAt
                    ? format(new Date(workflow.nextRunAt), "MMM d, HH:mm")
                    : "-"}
                </TableCell>
                <TableCell>
                  {workflow.currentWorkflowId ? (
                    <Chip
                      label="Running"
                      color="primary"
                      size="small"
                      sx={compactTableStyles.chip}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click
                      handleMenuOpen(e, workflow);
                    }}
                    sx={compactTableStyles.iconButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handlePauseResume}>
          {selectedWorkflow?.enabled ? "Pause" : "Resume"}
        </MenuItem>
        {selectedWorkflow?.enabled &&
          selectedWorkflow?.schedule !== "one-time" && (
            <MenuItem onClick={handleRunNow}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PlayArrowIcon fontSize="small" />
                Run Now
              </Box>
            </MenuItem>
          )}
        {selectedWorkflow?.schedule === "one-time" && (
          <MenuItem onClick={handleRunNow}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PlayArrowIcon fontSize="small" />
              Run Again
            </Box>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleEdit()}>Edit</MenuItem>
        <MenuItem onClick={() => handleViewState()}>View State</MenuItem>
        <MenuItem onClick={handleViewGptRequests}>View GPT Requests</MenuItem>
        <MenuItem onClick={handleViewActionLogs}>View Action Logs</MenuItem>
        <MenuItem onClick={handleViewTemporalWorkflows}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            View in Temporal
            <LaunchIcon fontSize="small" />
          </Box>
        </MenuItem>
        {selectedWorkflow?.schedule === "one-time" &&
          selectedWorkflow?.currentWorkflowId && (
            <MenuItem onClick={handleTerminate} sx={{ color: "error.main" }}>
              Terminate Running Workflow
            </MenuItem>
          )}
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Create Workflow Modal */}
      <CreateRecurringWorkflowModal
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={fetchWorkflows}
        workflowTypes={workflowTypes}
        isOneTime={isCreatingOneTime}
      />

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Recurring Workflow</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Schedule"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              fullWidth
              helperText='Use cron expressions (e.g., "0 * * * *") or intervals (e.g., "@every 5m")'
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={waitForPrevious}
                  onChange={(e) => setWaitForPrevious(e.target.checked)}
                />
              }
              label="Wait for previous run to complete"
            />

            <FormControl fullWidth>
              <Typography variant="subtitle2" gutterBottom>
                Timing Options
              </Typography>
              <RadioGroup
                value={timingMode}
                onChange={(e) => {
                  const mode = e.target.value as
                    | "none"
                    | "startTime"
                    | "startDelay"
                    | "phaseSec";
                  setTimingMode(mode);

                  // Set default values when enabling modes
                  if (mode === "startTime" && !startTime) {
                    const now = new Date();
                    const localDateTime = new Date(
                      now.getTime() - now.getTimezoneOffset() * 60000
                    )
                      .toISOString()
                      .slice(0, 16);
                    setStartTime(localDateTime);
                  }
                }}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio />}
                  label="No timing constraints"
                />
                <FormControlLabel
                  value="startTime"
                  control={<Radio />}
                  label="Set specific start time"
                />
                <FormControlLabel
                  value="startDelay"
                  control={<Radio />}
                  label="Set start delay (seconds)"
                />
                <FormControlLabel
                  value="phaseSec"
                  control={<Radio />}
                  label="Set phase offset (seconds)"
                />
              </RadioGroup>
            </FormControl>

            {timingMode === "startTime" && (
              <TextField
                label="Start Time (UTC)"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="⚠️ Please enter time in UTC timezone"
              />
            )}

            {timingMode === "startDelay" && (
              <TextField
                label="Start Delay (seconds)"
                type="number"
                value={startDelay}
                onChange={(e) => setStartDelay(Number(e.target.value))}
                fullWidth
                helperText="Delay from now (0 = start immediately)"
                slotProps={{ htmlInput: { min: 0 } }}
              />
            )}

            {timingMode === "phaseSec" && (
              <TextField
                label="Phase Offset (seconds)"
                type="number"
                value={phaseSec}
                onChange={(e) => setPhaseSec(Number(e.target.value))}
                fullWidth
                helperText="Phase offset for interval schedules (must be less than interval duration)"
                slotProps={{ htmlInput: { min: 0 } }}
              />
            )}

            <TextField
              label="Initial Parameters (JSON)"
              multiline
              rows={8}
              value={initialParams}
              onChange={(e) => setInitialParams(e.target.value)}
              fullWidth
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: 12,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* State Dialog */}
      <Dialog
        open={stateDialogOpen}
        onClose={() => {
          setStateDialogOpen(false);
          setSelectedWorkflow(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Workflow State</DialogTitle>
        <DialogContent>
          {selectedWorkflow && (
            <TextField
              multiline
              rows={20}
              fullWidth
              value={JSON.stringify(selectedWorkflow.state || {}, null, 2)}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "monospace",
                  fontSize: 12,
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </Box>
  );
};

export default RecurringWorkflowsTab;
